const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// La sessione viaggia in un cookie httpOnly: nessun token in localStorage (difesa dagli attacchi XSS).
export async function practiceApi(path, options = {}) {
  const isForm = options.body instanceof FormData;
  const response = await fetch(`${API}${path}`, { credentials: "include", ...options, headers: { ...(!isForm && { "Content-Type": "application/json" }) } });
  const json = await response.json().catch(() => null);
  if (!response.ok) throw new Error(typeof json?.detail === "string" ? json.detail : "Operazione non riuscita. Verifica i dati e riprova.");
  return json;
}
export async function openPracticeFile(file, download = false) {
  const response = await fetch(`${API}/files/${file.id}/download`, { credentials: "include" });
  if (!response.ok) throw new Error("Documento non disponibile");
  const blob = await response.blob();
  const safePreview = /^(application\/pdf|image\/(png|jpeg|webp)|text\/plain)(;|$)/i.test(file.content_type || "");
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  if (download || !safePreview) a.download = file.original_filename;
  else { a.target = "_blank"; a.rel = "noopener noreferrer"; }
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export async function fetchBrandingAsset(kind) {
  const response = await fetch(`${API}/settings/asset/${kind}`, { credentials: "include", cache: "no-store" });
  if (!response.ok) return "";
  return URL.createObjectURL(await response.blob());
}

export async function fetchUserSignature(userId) {
  const response = await fetch(`${API}/users/${userId}/signature`, { credentials: "include", cache: "no-store" });
  if (!response.ok || response.status === 204) return "";
  const blob = await response.blob();
  return blob.size ? URL.createObjectURL(blob) : "";
}

export async function downloadFinanceExport(year) {
  const response = await fetch(`${API}/finance/export${year ? `?year=${year}` : ""}`, { credentials: "include" });
  if (!response.ok) throw new Error("Export non disponibile");
  const url = URL.createObjectURL(await response.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = `Economia ${year === "all" ? "tutti gli anni" : year || ""}.xlsx`.replace(/\s+/g, " ").trim();
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

async function openPdf(response) {
  if (!response.ok) throw new Error("Stampa non disponibile");
  const url = URL.createObjectURL(await response.blob());
  const a = document.createElement("a");
  a.href = url; a.target = "_blank"; a.rel = "noopener noreferrer";
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export const openSiteReportPdf = (siteId, mode = "studio") =>
  fetch(`${API}/sites/${siteId}/report?mode=${mode}`, { credentials: "include" }).then(openPdf);


export async function openPracticePdf(practiceId) {
  await openPdf(await fetch(`${API}/practices/${practiceId}/pdf`, { credentials: "include" }));
}

export async function openPracticesPdf(ids) {
  await openPdf(await fetch(`${API}/practices/pdf-batch`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ids }) }));
}

export async function openQuotePdf(quoteId, fmt = "pdf") {
  await openDocumentFile(`/estimates/${quoteId}/pdf?fmt=${fmt}`, `Onorario professionale.${fmt}`, fmt);
}

export async function openQuoteDocument(quoteId, kind, fmt = "pdf") {
  const paths = {
    letter: `/estimates/${quoteId}/letter/pdf?fmt=${fmt}`,
    declaration: fmt === "docx" ? `/estimates/${quoteId}/declaration/docx` : `/estimates/${quoteId}/declaration/pdf`,
  };
  const names = { letter: `Lettera di incarico.${fmt}`, declaration: `Dichiarazione conferimento incarico.${fmt}` };
  await openDocumentFile(paths[kind] || paths.letter, names[kind] || names.letter, fmt);
}

export async function openDocumentFile(path, filename, fmt = "pdf", options = {}) {
  const response = await fetch(`${API}${path}`, { credentials: "include", headers: { ...(options.headers || {}) }, ...options });
  if (!response.ok) throw new Error("Documento non disponibile");
  const url = URL.createObjectURL(await response.blob());
  const a = document.createElement("a");
  a.href = url;
  if (fmt === "pdf") { a.target = "_blank"; a.rel = "noopener noreferrer"; }
  else a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export const TYPE_OPTIONS = [
  ["insegna", "INSEGNA"],
  ["permesso_di_costruire", "Permesso di Costruire"],
  ["pozzo_artesiano", "Pozzo artesiano"],
  ["sca_agibilita", "SCA/Agibilità"],
  ["sca_agibilita_parziale", "SCA/Agibilità parziale"],
  ["scia_edilizia", "SCIA edilizia"],
  ["altro", "Altro"],
];
export const TYPE_LABELS = { ...Object.fromEntries(TYPE_OPTIONS), scia: "SCIA", cila: "CILA", paesaggistica: "Paesaggistica" };
export const STATUS_OPTIONS = ["Da avviare", "In corso", "Sospesa", "Conclusa", "Da gestire"];
export const PROCEDURE_OPTIONS = ["Semplificata", "Ordinaria"];
export const COMPUTED_FIELDS = ["start_work_deadline", "end_work_deadline", "scia_end_deadline", "reminder_deadline",
  "alt_30_deadline", "alt_end_deadline", "asi_reminder_deadline"];
export const PERMIT_KINDS = [
  ["pdc", "PdC", "Permesso di costruire"],
  ["scia", "SCIA", "Segnalazione certificata"],
  ["pdc_sanatoria", "PdC in sanatoria", "Solo campi manuali"],
  ["scia_sanatoria", "SCIA in sanatoria", "Solo campi manuali"],
  ["scia_alternativa", "SCIA alternativa al PdC", "30 giorni + avvio lavori"],
];
export const PERMIT_FIELDS = {
  pdc: ["pdc_presentation_date", "permit_issue_date", "start_work_deadline", "permit_start_deadline_specific", "permit_start_extension",
    "work_start_date", "end_work_deadline", "permit_end_deadline_specific", "permit_end_extension", "work_end_date", "expiry_status_override"],
  scia: ["scia_submission_date", "work_start_date", "scia_end_deadline", "scia_end_deadline_specific", "scia_end_extension",
    "work_end_date", "scia_status_override"],
  pdc_sanatoria: ["pdc_presentation_date", "sanatoria_submission_date", "sanatoria_protocol", "sanatoria_decision_date", "sanatoria_outcome", "sanatoria_notes"],
  scia_sanatoria: ["sanatoria_submission_date", "sanatoria_protocol", "sanatoria_decision_date", "sanatoria_outcome", "sanatoria_notes"],
  scia_alternativa: ["alt_submission_date", "alt_effect_date", "alt_30_deadline", "alt_missing_acts", "alt_blocking_measures",
    "alt_works_allowed", "work_start_date", "alt_end_basis_date", "alt_end_deadline", "alt_end_deadline_specific",
    "alt_end_extension", "work_end_date"],
};
export const SUPERINTENDENCE_FIELDS = ["landscape_constraint", "landscape_procedure", "superintendence_submission_date",
  "landscape_authorization_date", "reminder_deadline", "reminder_sent_date", "reminder_status_override"];
export const ASI_FIELDS = ["asi_submission_date", "asi_reminder_days", "asi_reminder_deadline", "asi_reminder_sent_date"];
export const PERCENT_OPTIONS = [0.5, 1, 1.5, 2, 3, 4, 5, 8, 10];
export const MONEY_FIELDS = ["quote_amount", "agreed_amount", "works_amount", "works_direction_fee", "coordination_fee"];
export const PERCENT_FIELDS = ["works_direction_pct", "coordination_pct"];
export const OVERRIDE_OPTIONS = {
  expiry_status_override: ["REGOLARE", "IN SCADENZA", "URGENTE", "SCADUTA"],
  scia_status_override: ["REGOLARE", "IN SCADENZA", "URGENTE", "SCADUTA"],
  reminder_status_override: ["DA INVIARE", "INVIATO", "URGENTE", "SCADUTO"],
};
export const FIELD_LABELS = {
  name: "Titolo pratica", type: "Tipo pratica", type_other: "Tipologia personalizzata", practice_number: "Numero / protocollo", status: "Stato",
  opening_date: "Data apertura", responsible: "Responsabile",
  client_name: "Committente", client_fiscal_code: "Codice fiscale / P. IVA", site: "Cantiere", site_address: "Indirizzo intervento", municipality: "Comune",
  coordinator_name: "Coordinatore", contractor_name: "Impresa", landscape_constraint: "Vincolo paesaggistico",
  landscape_procedure: "Procedura paesaggistica", superintendence_submission_date: "Invio Soprintendenza",
  landscape_authorization_date: "Rilascio autorizzazione paesaggistica",
  pdc_presentation_date: "Data di presentazione", permit_issue_date: "Data rilascio PdC", start_work_deadline: "Termine inizio lavori (1 anno dal rilascio)", work_start_date: "Data inizio lavori",
  permit_start_deadline_specific: "Termine inizio lavori indicato nel titolo", permit_start_extension: "Proroga termine inizio lavori",
  permit_end_deadline_specific: "Termine fine lavori indicato nel titolo", permit_end_extension: "Proroga termine fine lavori",
  scia_end_deadline_specific: "Termine fine lavori indicato nella SCIA", scia_end_extension: "Proroga termine fine lavori SCIA",
  sanatoria_submission_date: "Data presentazione istanza", sanatoria_protocol: "Protocollo", sanatoria_decision_date: "Data provvedimento",
  sanatoria_outcome: "Esito", sanatoria_notes: "Note sanatoria",
  alt_submission_date: "Data presentazione", alt_effect_date: "Decorrenza dei 30 giorni",
  alt_30_deadline: "Termine dei 30 giorni", alt_missing_acts: "Atti di assenso mancanti",
  alt_blocking_measures: "Provvedimenti ostativi", alt_works_allowed: "Condizioni verificate: avvio lavori consentito",
  alt_end_basis_date: "Decorrenza del termine di fine lavori", alt_end_deadline: "Termine fine lavori (3 anni)",
  alt_end_deadline_specific: "Termine fine lavori indicato nel titolo", alt_end_extension: "Proroga termine fine lavori",
  superintendence_enabled: "Invio alla Soprintendenza", asi_enabled: "Invio al Consorzio ASI", is_site: "La pratica è un cantiere",
  asi_submission_date: "Data invio ASI", asi_reminder_days: "Giorni prima del sollecito",
  asi_reminder_deadline: "Scadenza sollecito ASI", asi_reminder_sent_date: "Data sollecito ASI inviato",
  end_work_deadline: "Termine fine lavori (3 anni dall'inizio lavori)", work_end_date: "Data fine lavori effettiva",
  scia_submission_date: "Data presentazione SCIA", scia_end_deadline: "Termine fine lavori SCIA (3 anni)",
  reminder_deadline: "Scadenza sollecito Soprintendenza", reminder_sent_date: "Data sollecito inviato",
  quote_amount: "Importo preventivo", agreed_amount: "Importo concordato", works_amount: "Importo lavori",
  works_direction_pct: "Direzione lavori %", works_direction_fee: "Compenso direzione lavori",
  coordination_pct: "Coordinamento %", coordination_fee: "Compenso coordinamento",
  expiry_status_override: "Stato scadenza (forzato)", scia_status_override: "Stato SCIA (forzato)", reminder_status_override: "Stato sollecito (forzato)",
  notes: "Note", summary: "Oggetto (sintetico)", description: "Descrizione estesa",
};
const STATUS_TONES = {
  SCADUTA: "danger", SCADUTO: "danger", SCADUTE: "danger",
  URGENTE: "urgent", "DA VERIFICARE": "urgent",
  "IN SCADENZA": "warning", "DA INVIARE": "warning", "DA EFFETTUARE": "warning", "DA COMPLETARE": "warning",
  REGOLARE: "success", APERTA: "success", INVIATO: "success", COMPLETATA: "success", CONCLUSA: "success",
  AUTORIZZATO: "success",
  EFFETTUATA: "success", ACCETTATO: "success", DEFINITIVO: "success", CONSEGNATO: "success",
  PROGRAMMATA: "neutral", "DA DEFINIRE": "neutral", BOZZA: "neutral",
};
export const badgeTone = status => STATUS_TONES[String(status ?? "").trim().toUpperCase()] || "neutral";
/* Ogni menu a tendina di clienti, pratiche, cantieri e pozzi va presentato in ordine alfabetico. */
export const byName = (list = [], key = "name") => [...list].sort((a, b) =>
  String(a?.[key] || "").localeCompare(String(b?.[key] || ""), "it", { sensitivity: "base", numeric: true }));

export const euro = (value) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", useGrouping: true }).format(Number(value) || 0);
export const formatPercent = (value) => value == null || value === "" ? "Da definire" : `${String(value).replace(".", ",")} %`;
export const formatDate = (value) => value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value.split("-").reverse().join("/") : value || "Da definire";
export const displayValue = (key, value) => value === true ? "Sì" : value === false ? "No"
  : key === "type" ? TYPE_LABELS[value] || value || "Da definire"
  : MONEY_FIELDS.includes(key) ? (value == null || value === "" ? "Da compilare" : euro(value))
  : PERCENT_FIELDS.includes(key) ? formatPercent(value)
  : key === "pdc_presentation_date" && !value ? "Da compilare"
  : /date|deadline/.test(key) ? formatDate(value) : value || "Da compilare";